import React, { useCallback, useEffect, useState, useRef } from 'react';
import ModalComponent from '../../components/ModalComponent';
import { Line } from 'react-chartjs-2';
import { getModalLine } from '../../services/analysis';
import DatePicker from '../../components/Form/DatePicker';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import formatNumber from '../../utils/formatNumber';
interface Props {
    show?: boolean,
    onHide: () => void,
    options?: any,
    data?: any,
    title?: string
    calc?: string,
    farmLocal?: string,
    fromGraph?: string
}

const ModalChart: React.FC<Props> = ({ show, onHide, data, options, title, calc, farmLocal, fromGraph }) => {
    const handleClose = () => onHide();
    const [lineData, setLineData] = useState(data);
    const [lineOptions, setLineOptions] = useState(options);

    // Persisted state for period dates
    const [fromYearLocal, setFromYearLocal] = useState(() => {
        const saved = localStorage.getItem('modalChart_fromYear');
        return saved || String(new Date().getFullYear() - 5);
    });
    const [toYearLocal, setToYearLocal] = useState(() => {
        const saved = localStorage.getItem('modalChart_toYear');
        return saved || String(new Date().getFullYear());
    });
    const [fromMonthLocal, setFromMonthLocal] = useState(() => {
        const saved = localStorage.getItem('modalChart_fromMonth');
        return saved || String(new Date().getMonth() + 1);
    });
    const [toMonthLocal, setToMonthLocal] = useState(() => {
        const saved = localStorage.getItem('modalChart_toMonth');
        return saved || String(new Date().getMonth() + 1);
    });
    const [groupBy, setGroupBy] = useState(() => {
        const saved = localStorage.getItem('modalChart_groupBy');
        return saved || 'month';
    });

    const [dataError, setDataError] = useState(false);
    const [dataErrorMessage, setDataErrorMessage] = useState('');
    const [render, setRender] = useState(false);
    const navigate = useNavigate();

    // Persisted state for chart height resize
    const [chartHeight, setChartHeight] = useState(() => {
        const saved = localStorage.getItem('modalChart_chartHeight');
        return saved ? Number(saved) : 400;
    });
    const [isResizing, setIsResizing] = useState(false);
    const resizeStartY = useRef(0);
    const resizeStartHeight = useRef(chartHeight);

    // Persisted state for table visibility
    const [showTable, setShowTable] = useState(() => {
        const saved = localStorage.getItem('modalChart_showTable');
        return saved ? JSON.parse(saved) : false;
    });

    // Persisted state for modal expansion
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = localStorage.getItem('modalChart_isExpanded');
        return saved ? JSON.parse(saved) : false;
    });

    // Persist chart height changes
    useEffect(() => {
        localStorage.setItem('modalChart_chartHeight', String(chartHeight));
    }, [chartHeight]);

    // Persist table visibility changes
    useEffect(() => {
        localStorage.setItem('modalChart_showTable', JSON.stringify(showTable));
    }, [showTable]);

    // Persist modal expansion changes
    useEffect(() => {
        localStorage.setItem('modalChart_isExpanded', JSON.stringify(isExpanded));
    }, [isExpanded]);

    // Persist groupBy selection changes
    useEffect(() => {
        localStorage.setItem('modalChart_groupBy', groupBy);
    }, [groupBy]);

    // Persist date range changes
    useEffect(() => {
        localStorage.setItem('modalChart_fromYear', fromYearLocal);
    }, [fromYearLocal]);

    useEffect(() => {
        localStorage.setItem('modalChart_toYear', toYearLocal);
    }, [toYearLocal]);

    useEffect(() => {
        localStorage.setItem('modalChart_fromMonth', fromMonthLocal);
    }, [fromMonthLocal]);

    useEffect(() => {
        localStorage.setItem('modalChart_toMonth', toMonthLocal);
    }, [toMonthLocal]);

    useEffect(() => {
        // Only fetch data if modal is open and all required parameters are present
        if (show && calc && fromYearLocal && toYearLocal && fromMonthLocal && toMonthLocal) {
            getData();
        }
    }, [show, groupBy, fromYearLocal, fromMonthLocal, toYearLocal, toMonthLocal]);

    const getData = () => {
        // Validate required parameters before making API call
        if (!calc || !fromYearLocal || !toYearLocal || !fromMonthLocal || !toMonthLocal) {
            return;
        }

        setRender(false);
        getModalLine(calc, fromYearLocal, toYearLocal, fromMonthLocal, toMonthLocal, farmLocal, groupBy, fromGraph)
            .then(({ data }) => {
                // clear errors 
                setDataError(false);
                setDataErrorMessage('');
                // Explicit mapping between Japanese labels and English field names
                const jaEnMap: Record<string, string> = {
                    // Ranking chart
                    '上位10%': 'top10',
                    '中央値': 'center',
                    '下位10%': 'bottom10',
                    '個人値': 'current_farm',
                    // Region chart
                    '1 北海道': 'region_1',
                    '2 東北': 'region_2',
                    '3 北関東': 'region_3',
                    '4 南関東': 'region_4',
                    '5 北陸': 'region_5',
                    '6 東海・近畿': 'region_6',
                    '7 九州・四国': 'region_7',
                    // Scale chart
                    '1 250頭以下': 'item_1',
                    '2 251頭～500頭': 'item_2',
                    '3 501頭～1000頭': 'item_3',
                    '4 1001頭以上': 'item_4'
                };

                let tempdata: { datasets: any[], labels: any[] } = {
                    datasets: lineData.datasets,
                    labels: [],
                };

                tempdata.labels = data.axisX.map((item: any) => String(item));

                // data.datasets setup - build arrays from data for each field
                const itemValues = Object.values(data.data) as any[];
                const fieldNames = Object.values(jaEnMap);
                const globalArray: Record<string, any[]> = {};

                fieldNames.forEach((field) => {
                    globalArray[field] = itemValues.map((item) => item[field]);
                });

                tempdata.datasets = tempdata.datasets.map((dataset: any) => {
                    const fieldName = jaEnMap[dataset.label];
                    dataset.data = globalArray[fieldName];
                    return dataset;
                });

                // set lineData and lineOptions
                setLineData({
                    labels: tempdata.labels,
                    datasets: tempdata.datasets
                });

                setLineOptions({
                    devicePixelRatio: 4,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: true,
                                font: {
                                    size: 16
                                }
                            }
                        }
                    },
                });
            })
            .catch((e) => {
                setDataError(true);
                setDataErrorMessage(e.response.data.message);
            }).finally(() => {
                setRender(true);
            }
            );
    };

    const handleDateChange = (e: string, range: 'from' | 'to') => {
        const [year, month] = e.split('-');
        switch (groupBy) {
            case 'month':
                if (range === 'from') {
                    setFromYearLocal(year);
                    setFromMonthLocal(month);
                } else {
                    setToYearLocal(year);
                    setToMonthLocal(month);
                }
                break;
            case 'quarter':
            case 'year':
                range === "from" ? setFromYearLocal(year) : setToYearLocal(year)
                break;
        }
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>, range: 'from' | 'to') => {
        const selectedYear = e.target.value;
        if (range === 'from') {
            setFromYearLocal(selectedYear);
        } else {
            setToYearLocal(selectedYear);
        }
    };

    const openPrintPage = () => {
        // Build URL with parameters for direct access and refresh support
        const params = new URLSearchParams({
            title: title || '',
            fromYear: fromYearLocal,
            toYear: toYearLocal,
            fromMonth: fromMonthLocal,
            toMonth: toMonthLocal,
            groupBy: groupBy,
            ...(calc && { calc }),
            ...(farmLocal && { farm: farmLocal }),
            ...(fromGraph && { fromGraph }),
        });

        navigate(`/print-page?${params.toString()}`, {
            state: {
                title: title,
                fromYear: fromYearLocal,
                toYear: toYearLocal,
                fromMonth: fromMonthLocal,
                toMonth: toMonthLocal,
                lineData: lineData,
                lineOptions: lineOptions,
                groupBy: groupBy,
                showTable: showTable,
                chartHeight: chartHeight,
                isExpanded: isExpanded,
            }
        });
    };

    // Chart resize handlers
    const handleResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        resizeStartY.current = e.clientY;
        resizeStartHeight.current = chartHeight;
    };

    useEffect(() => {
        const handleResizeMove = (e: MouseEvent) => {
            if (!isResizing) return;

            const deltaY = e.clientY - resizeStartY.current;
            const newHeight = resizeStartHeight.current + deltaY;

            // Clamp height between 250px and 800px
            const clampedHeight = Math.min(Math.max(newHeight, 250), 800);
            setChartHeight(clampedHeight);
        };

        const handleResizeEnd = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
        };
    }, [isResizing]);

    // Render data table from chart data
    const renderDataTable = () => {
        if (!lineData || !lineData.labels || !lineData.datasets) return null;

        return (
            <div className="table-responsive" style={{ marginTop: '30px' }}>
                <table className="table table-striped table-bordered" style={{ fontSize: '1rem' }}>
                    <thead className="table-dark">
                        <tr>
                            <th style={{ padding: '8px 12px' }}>期間</th>
                            {lineData.datasets.map((dataset: any, idx: number) => (
                                <th key={idx} style={{ padding: '8px 12px' }}>{dataset.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {lineData.labels.map((label: string, rowIdx: number) => (
                            <tr key={rowIdx}>
                                <td className="fw-bold" style={{ padding: '8px 12px' }}>{label}</td>
                                {lineData.datasets.map((dataset: any, colIdx: number) => (
                                    <td key={colIdx} className="text-end" style={{ padding: '8px 12px' }}>
                                        {dataset.data[rowIdx] !== null && dataset.data[rowIdx] !== undefined
                                            ? formatNumber(dataset.data[rowIdx])
                                            : '-'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Footer content with table toggle
    const footerContent = (
        <div
            onClick={() => setShowTable(!showTable)}
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
                transition: 'background-color 0.2s',
                height: '38px',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
        >
            <span style={{ fontWeight: '500', fontSize: '14px', lineHeight: '1' }}>データ一覧表示：</span>
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: showTable ? '#0d6efd' : '#6c757d',
                color: '#fff',
                fontSize: '12px',
                transition: 'background-color 0.3s',
                lineHeight: '1',
            }}>
                {showTable ? '　●' : '●　'}
            </span>
        </div>
    );

    return (
        <>
            <ModalComponent
                title={title}
                size="xl"
                cancelButtonText="閉じる"
                isExpanded={isExpanded}
                footerContent={footerContent}
                body={
                    <div>
                        {dataError && (<>
                            <div className="alert w-100 p-2 fs-xs text-white" style={{ backgroundColor: '#ff6565' }}>
                                {dataErrorMessage}
                            </div>
                        </>)}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            {/* select groupBy */}
                            <div className="d-flex border border-dark rounded overflow-hidden">
                                <button
                                    className={`btn ${groupBy === 'month' ? 'btn-dark' : 'btn-light'}`}
                                    onClick={() => setGroupBy('month')}
                                >
                                    単月
                                </button>
                                <button
                                    className={`btn ${groupBy === 'quarter' ? 'btn-dark' : 'btn-light'}`}
                                    onClick={() => setGroupBy('quarter')}
                                >
                                    四半期
                                </button>
                                <button
                                    className={`btn ${groupBy === 'year' ? 'btn-dark' : 'btn-light'}`}
                                    onClick={() => setGroupBy('year')}
                                >
                                    単年
                                </button>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {/* date for groupBy month */}
                                {groupBy === 'month' &&
                                    (<>
                                        <DatePicker
                                            id="fromDate"
                                            initialDate={fromYearLocal + '/' + fromMonthLocal}
                                            onChange={(e) => handleDateChange(e, 'from')}
                                            dependencies={[groupBy]}
                                        />
                                        <span>~</span>
                                        <DatePicker
                                            id="toDate"
                                            initialDate={toYearLocal + '/' + toMonthLocal}
                                            onChange={(e) => handleDateChange(e, 'to')}
                                            dependencies={[groupBy]}
                                        />
                                    </>)}

                                {/* date for groupBy quarter and year */}
                                {(groupBy === 'quarter' || groupBy === 'year') && (
                                    <>
                                        <select className="form-select" value={fromYearLocal} onChange={(e) => handleYearChange(e, 'from')}>
                                            {Array.from({ length: 50 }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return <option key={year} value={year}>{year}</option>;
                                            })}
                                        </select>
                                        <span>~</span>
                                        <select className="form-select" value={toYearLocal} onChange={(e) => handleYearChange(e, 'to')}>
                                            {Array.from({ length: 50 }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return <option key={year} value={year}>{year}</option>;
                                            })}
                                        </select>
                                    </>
                                )}
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {/* Botão de expandir/contrair modal */}
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={(e) => {
                                        setIsExpanded(!isExpanded);
                                        e.currentTarget.blur();
                                    }}
                                    title={isExpanded ? 'モーダルを縮小' : 'モーダルを拡大'}
                                    style={{
                                        padding: '8px 12px',
                                        height: '38px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        gap: '4px'
                                    }}
                                >
                                    {isExpanded ? (
                                        <>
                                            <span>▶︎</span>
                                            <span>横幅</span>
                                            <span>◀︎</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>◀︎</span>
                                            <span>横幅</span>
                                            <span>▶︎</span>
                                        </>
                                    )}
                                </button>

                                <button type="button" className="btn btn-primary" onClick={openPrintPage} style={{ height: '38px' }}>印刷</button>
                            </div>
                        </div>

                        {/* graph */}
                        {render ? (
                            <>
                                <div
                                    className="chart w-100 position-relative"
                                    style={{
                                        height: `${chartHeight}px`,
                                        minHeight: '250px',
                                        maxHeight: '800px',
                                        paddingBottom: '25px'
                                    }}
                                >
                                    <Line id="modalChartLine" data={lineData} options={lineOptions} />

                                    {/* Resize Handle */}
                                    <div
                                        onMouseDown={handleResizeStart}
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '20px',
                                            cursor: 'ns-resize',
                                            backgroundColor: isResizing ? 'rgba(13, 110, 253, 0.1)' : 'transparent',
                                            transition: 'background-color 0.2s',
                                            borderTop: '2px solid #dee2e6',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isResizing) {
                                                e.currentTarget.style.backgroundColor = 'rgba(233, 236, 239, 0.5)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isResizing) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        <div style={{
                                            width: '60px',
                                            height: '4px',
                                            backgroundColor: '#6c757d',
                                            borderRadius: '2px',
                                        }} />
                                    </div>
                                </div>

                                {/* Tabela de dados */}
                                {showTable && renderDataTable()}
                            </>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center w-100 h-100" style={{ minHeight: '250px' }}>
                                <Spinner animation="border" />
                            </div>
                        )}
                    </div >
                }
                show={show}
                closeModal={handleClose}
            />
        </>
    );
};

export default ModalChart;
